import React from "react";
import { Link } from "react-router-dom";
import { Leaf } from "lucide-react";
import { useSiteContent } from "../hooks/useSiteContent";

/**
 * Editorial footer.
 *  - Three-generation photo as a soft background/hero
 *  - Pale taupe + ivory + sage overlay so text breathes
 *  - Three quiet columns, generous whitespace, subtle hover
 */
export default function Footer() {
  const c = useSiteContent();
  const tagline = c.footer_tagline || "Gentle connection across generations.";
  const email = c.footer_email || "hello@gentlegito.com";

  const linkCls =
    "relative inline-block text-foreground/75 hover:text-foreground transition-colors duration-500 " +
    "after:content-[''] after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-px " +
    "after:bg-foreground/30 after:scale-x-0 hover:after:scale-x-100 after:origin-left " +
    "after:transition-transform after:duration-500";

  return (
    <footer
      data-testid="site-footer"
      className="relative isolate overflow-hidden border-t border-border/40"
    >
      {/* Background photo */}
      {c.footer_image && (
        <img
          src={c.footer_image}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover ken-burns-slow -z-20"
        />
      )}

      {/* Layered soft overlays — pale taupe / ivory / sage wash so type stays readable */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, hsl(60 33% 97% / 0.78) 0%, hsl(36 27% 93% / 0.82) 45%, hsl(95 21% 75% / 0.55) 100%)",
        }}
      />
      <div className="absolute inset-0 -z-10 light-sweep pointer-events-none opacity-40" aria-hidden="true" />

      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-28 sm:py-36 lg:py-44">
        <div className="grid md:grid-cols-3 gap-16 md:gap-20 lg:gap-28">
          {/* Left — Brand */}
          <div className="md:col-span-1">
            <Link
              to="/"
              data-testid="footer-brand"
              className="inline-flex items-center gap-3 group"
              aria-label="GentleGiTo — home"
            >
              <Leaf
                size={18}
                strokeWidth={1.4}
                className="text-primary transition-transform duration-700 group-hover:rotate-[8deg]"
              />
              <span className="font-serif text-2xl sm:text-[1.7rem] tracking-tight text-foreground">
                GentleGi<span className="italic text-primary">T</span>o
              </span>
            </Link>
            <p className="mt-6 font-serif italic text-base sm:text-lg leading-relaxed text-foreground/70 max-w-xs">
              {tagline}
            </p>
          </div>

          {/* Middle — Journal */}
          <div>
            <p
              data-testid="footer-col-journal"
              className="text-[11px] uppercase tracking-[0.32em] text-primary mb-7"
            >
              Journal
            </p>
            <ul className="space-y-4 text-sm sm:text-[15px] tracking-wide">
              <li>
                <Link
                  to="/journal?audience=grandparents"
                  data-testid="footer-grandparents"
                  className={linkCls}
                >
                  Grandparents
                </Link>
              </li>
              <li>
                <Link
                  to="/journal?audience=parents"
                  data-testid="footer-parents"
                  className={linkCls}
                >
                  Parents
                </Link>
              </li>
              <li>
                <Link
                  to="/journal?audience=little-ones"
                  data-testid="footer-little-ones"
                  className={linkCls}
                >
                  Little Ones
                </Link>
              </li>
            </ul>
          </div>

          {/* Right — Connect */}
          <div>
            <p
              data-testid="footer-col-connect"
              className="text-[11px] uppercase tracking-[0.32em] text-primary mb-7"
            >
              Connect
            </p>
            <ul className="space-y-4 text-sm sm:text-[15px] tracking-wide">
              <li>
                <a href="/#about" data-testid="footer-about" className={linkCls}>
                  About
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${email}`}
                  data-testid="footer-contact"
                  className={linkCls}
                >
                  Contact
                </a>
              </li>
              <li>
                <a
                  href="/#signup"
                  data-testid="footer-subscribe"
                  className={linkCls}
                >
                  Subscribe
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* A single hairline closing — quiet ending, not a sitemap */}
        <div className="mt-24 sm:mt-32 pt-8 border-t border-foreground/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-[11px] tracking-[0.25em] uppercase text-foreground/55">
            {c.footer_domain || "GentleGiTo.com"} · © {new Date().getFullYear()}
          </p>
          <p className="text-xs italic font-serif text-foreground/55">
            {c.footer_italic || "Connection begins long before memory."}
          </p>
        </div>
      </div>
    </footer>
  );
}
